import { Page, pickSeoProps } from '@components/Page'
import { LandingPageHero } from '@components/_rcpo/LandingPageHero'
import { RCPOLatestWatches } from '@components/_rcpo/LatestWatches'
import { RCPOPage } from '@components/_rcpo/Page'
import { buildLanguageAlternateNavigation } from '@lib/build-language-alternates'
import { getServerSideTranslations } from '@lib/get-server-side-translations'
import { Item, SingleItemResponse, getPageByCodename } from '@lib/kontent'
import { GetStaticProps, NextPage } from 'next'
import { useRouter } from 'next/router'
import { RolexPushBannerGrid } from '@components/_rolex/push-banner-grid'
import { RolexPushBannerGrid as RolexPushBannerGridModel } from '@lib/kontent/models/RolexPushBannerGrid'
import KeepExploring from '@components/_rcpo/Sliders/KeepExploring'
import { cn } from '@lib/cn'
import { RolexRcpoLandingPageV7 } from '@lib/kontent/models/RolexRcpoLandingPageV7'
import { RolexPageIntro } from '@components/_rolex/PageIntro'
import { RolexPageIntro as RolexPageIntroModel } from '@lib/kontent/models/RolexPageIntro'
import { RolexKeepExploringSliderItem } from '@lib/kontent/models/RolexKeepExploringSliderItem'
import { RolexPromoBanner } from '@components/_rolex/PromoBanner'
import { RolexPromoBanner as RolexPromoBannerModel } from '@lib/kontent/models/RolexPromoBanner'

type RCPOHomepageProps = SingleItemResponse<any> // RolexRcpoLandingPageV7 | RcpoLandingPage

const RCPOHomepage: NextPage<RCPOHomepageProps> = ({ item, modular_content }) => {
  const router = useRouter()

  const pageIntro = modular_content[item.elements.intro_section.value[0]]
    .elements satisfies RolexPageIntroModel

  const contactUsBanner = modular_content[
    item.elements.contact_push_banners.value[0]
  ] satisfies Item<RolexPushBannerGridModel>

  const keepExploringItems = item.elements.keep_exploring_items.value.map(
    (key) => modular_content[key].elements,
  ) satisfies RolexKeepExploringSliderItem[]

  const crossLinkingBanner =
    item.elements.cross_linking_banner?.value[0] &&
    (modular_content[item.elements.cross_linking_banner.value[0]]
      .elements satisfies RolexPromoBannerModel)

  return (
    <Page
      {...pickSeoProps(item.elements)}
      languageAlternates={buildLanguageAlternateNavigation(router.locales, 'rcpo.index.url')}
      noindex={router.locale === 'de'}
    >
      <RCPOPage pageType="home page">
        <div className={cn('bg-rolexLightBeige pb-[60px]', 'lg:pb-[90px]')}>
          <LandingPageHero
            mobileImage={item.elements.mobile_image}
            desktopImage={item.elements.desktop_image}
            title={item.elements.title.value}
            buttonText={item.elements.button_text.value}
          />
          <RolexPageIntro
            title={pageIntro.title.value}
            description={pageIntro.description}
            titleTag={pageIntro.title_tag.value[0].codename}
          />
          <RCPOLatestWatches buttonTitle={item.elements.latest_watches_button_text.value} />
          <div className={cn('flex flex-col gap-y-10 py-[60px]', 'lg:!py-[90px]')}>
            {item.elements.featured_push_banner_item.value.map((key) => (
              <RolexPushBannerGrid
                {...modular_content[key].elements}
                banners={modular_content[key].elements.items.value.map(
                  (key) => modular_content[key].elements,
                )}
                className="!py-0"
              />
            ))}
          </div>
          <RolexPushBannerGrid
            {...contactUsBanner.elements}
            banners={contactUsBanner.elements.items.value.map(
              (key) => modular_content[key].elements,
            )}
            className="!py-0"
          />
        </div>
        {crossLinkingBanner && (
          <div className="mb-[60px] lg:mb-[90px]">
            <RolexPromoBanner item={crossLinkingBanner} />
          </div>
        )}
        <KeepExploring items={keepExploringItems} title={item.elements.title_7d9cc03.value} />
      </RCPOPage>
    </Page>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale, preview }) => {
  const lang = locale === 'de' ? 'en' : locale

  try {
    const content = await getPageByCodename<RolexRcpoLandingPageV7>({
      codename: 'rolex_rcpo_landing_page_v7',
      lang: lang satisfies string,
      preview,
    })

    return {
      props: {
        ...content,
        ...(await getServerSideTranslations(lang satisfies string)),
      },
    }
  } catch (error) {
    throw error
  }
}

export default RCPOHomepage
